// client/src/components/LeaderboardTab.jsx
// "טבלת מובילים" — students ranked by the camp's chosen METRIC:
//   BALANCE       current points (credits − debits)
//   EARNED_TOTAL  all credits since the program began
//   EARNED_SINCE  credits from a chosen date
//
// The metric picker previews instantly (query param); "שמור כהגדרה קבועה"
// persists it server-side so every manager, on every computer, sees the same
// board. The top three are pulled out into a podium (#2 #1 #3 left-to-right)
// and the rest fall into a list below with progress bars scaled to the
// leader's score. A search + sort strip (shared ListToolbar) sits above.
//
// Students with zero transactions still appear (score: 0). Useful as a
// "who hasn't started yet" view.

import { useEffect, useMemo, useState } from 'react';
import { useLeaderboard, useSaveLeaderboardSettings } from '../hooks/useLeaderboard.js';
import Spinner from './Spinner.jsx';
import ListToolbar from './ListToolbar.jsx';
import { formatMoney, formatDate } from '../lib/format.js';
import { matchesText, sortRows } from '../lib/listTools.js';
import { TrophyIcon } from './Icons.jsx';

const METRICS = [
  { key: 'BALANCE',      label: 'נקודות נוכחיות', hint: 'יתרה: זיכויים פחות חיובים' },
  { key: 'EARNED_TOTAL', label: 'מצטבר מתחילת התוכנית', hint: 'כל הזיכויים — קנייה לא מורידה בדירוג' },
  { key: 'EARNED_SINCE', label: 'מתאריך מסוים', hint: 'זיכויים מהתאריך שנבחר ואילך' },
];
const METRIC_LABEL = Object.fromEntries(METRICS.map((m) => [m.key, m.label]));

const SORT_OPTIONS = [
  { key: 'score', label: 'דירוג' },
  { key: 'name', label: 'שם' },
  { key: 'className', label: 'כיתה' },
  { key: 'balance', label: 'יתרה נוכחית' },
  { key: 'credit', label: 'סה״כ זיכויים' },
  { key: 'debit', label: 'סה״כ חיובים' },
];
const SORT_GETTERS = {
  score: (r) => r.score,
  name: (r) => `${r.lastName} ${r.firstName}`,
  className: (r) => r.className,
  balance: (r) => r.balance,
  credit: (r) => r.credit,
  debit: (r) => r.debit,
};

/** ISO → 'YYYY-MM-DD' in LOCAL time for <input type="date"> (UTC slicing would
 *  show the previous day for an Israel-midnight date). */
function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function LeaderboardTab({ campId, currency }) {
  // view = null until the saved settings arrive, then mirrors them; the
  // picker edits `view` and the query follows.
  const [view, setView] = useState(null);
  const lb = useLeaderboard(campId, view);
  const save = useSaveLeaderboardSettings(campId);
  const settings = lb.data?.settings;

  useEffect(() => {
    if (view === null && settings) setView({ metric: settings.metric, since: settings.since });
  }, [settings, view]);

  const [classFilter, setClassFilter] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'score', dir: 'desc' });

  const data = lb.data?.leaderboard ?? [];
  const allClasses = useMemo(
    () => [...new Set(data.map((s) => s.className).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'he')),
    [data]
  );
  const rows = useMemo(() => {
    const filtered = data.filter(
      (s) =>
        (!classFilter || s.className === classFilter) &&
        matchesText(s, q, [(r) => `${r.firstName} ${r.lastName}`, 'className'])
    );
    // 'score' desc is the server's ranking (ties by name) — keep it verbatim.
    if (sort.key === 'score' && sort.dir === 'desc') return filtered;
    return sortRows(filtered, sort, SORT_GETTERS);
  }, [data, classFilter, q, sort]);

  if (lb.isLoading && !lb.data) return <Spinner label="טוען טבלת מובילים..." />;
  if (lb.isError) {
    return (
      <div className="card-danger p-4 text-joy-900 text-sm">
        שגיאה: {lb.error.message}
      </div>
    );
  }

  const activeMetric = view?.metric || lb.data?.metric || 'BALANCE';
  const isRanked = sort.key === 'score' && sort.dir === 'desc' && !q;
  const top3 = isRanked ? rows.slice(0, 3) : [];
  const rest = isRanked ? rows.slice(3) : rows;
  const leaderScore = Math.max(rows.reduce((m, r) => Math.max(m, r.score || 0), 0), 1);
  const avg = rows.length ? Math.round(rows.reduce((s, r) => s + (r.score || 0), 0) / rows.length) : 0;
  const dirty =
    settings && view &&
    (view.metric !== settings.metric ||
      (view.metric === 'EARNED_SINCE' && toDateInput(view.since) !== toDateInput(settings.since)));

  return (
    <div className="space-y-5">
      <MetricPicker
        view={view || { metric: activeMetric, since: null }}
        onChange={setView}
        dirty={!!dirty}
        saving={save.isPending}
        saveError={save.isError ? save.error?.message : null}
        onSave={() => save.mutate({ metric: view.metric, since: view.since })}
        onRevert={() => setView({ metric: settings.metric, since: settings.since })}
      />

      <ListToolbar
        search={{ value: q, onChange: setQ, placeholder: 'חיפוש תלמיד או כיתה...' }}
        sort={{ value: sort, onChange: setSort, options: SORT_OPTIONS }}
        count={{ shown: rows.length, total: data.length, noun: 'תלמידים' }}
      >
        {allClasses.length > 0 && (
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            className="input h-10 py-0 w-auto"
            aria-label="סינון לפי כיתה"
          >
            <option value="">כל הכיתות</option>
            {allClasses.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </ListToolbar>

      {rows.length === 0 ? (
        <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
          <div className="mx-auto w-16 h-16 rounded-3xl bg-gold-gradient text-white grid place-items-center mb-4 shadow-pop">
            <TrophyIcon className="w-8 h-8" />
          </div>
          <h2 className="text-lg font-bold text-ink-900">
            {classFilter || q ? 'לא נמצאו תלמידים מתאימים' : 'אין עדיין תלמידים'}
          </h2>
        </div>
      ) : (
        <>
          <div className="text-sm text-ink-500 px-1">
            מדורג לפי <strong className="text-ink-700">{METRIC_LABEL[activeMetric]}</strong>
            {activeMetric === 'EARNED_SINCE' && lb.data?.since && (
              <> מ-<strong className="text-ink-700 num">{formatDate(lb.data.since)}</strong></>
            )}
            {' · '}ממוצע <strong className="text-ink-700">{formatMoney(avg, currency)}</strong>
            {lb.isFetching && <span className="ms-2 text-ink-400">מעדכן…</span>}
          </div>

          {top3.length > 0 && <Podium top3={top3} currency={currency} />}

          {rest.length > 0 && (
            <ul className="card divide-y divide-ink-100 overflow-hidden">
              {rest.map((s) => (
                <RankRow key={s.id} s={s} leaderScore={leaderScore} currency={currency} metric={activeMetric} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/* ───── Metric picker ────────────────────────────────────────── */

function MetricPicker({ view, onChange, dirty, saving, saveError, onSave, onRevert }) {
  const needsDate = view.metric === 'EARNED_SINCE';
  const sinceValue = toDateInput(view.since);
  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="eyebrow">מה מדרג את הלוח</p>
          <h2 className="h-section mt-1">בחירת מדד</h2>
        </div>
        {dirty && (
          <div className="flex items-center gap-2 animate-slide-up">
            <button type="button" className="btn-ghost btn-sm" onClick={onRevert} disabled={saving}>
              בטל
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={onSave}
              disabled={saving || (needsDate && !sinceValue)}
              title="ההגדרה תישמר לתוכנית — תופיע כך לכל המנהלים, מכל מחשב"
            >
              {saving ? 'שומר...' : 'שמור כהגדרה קבועה'}
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {METRICS.map((m) => {
          const active = view.metric === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onChange({ metric: m.key, since: m.key === 'EARNED_SINCE' ? view.since : null })}
              className={`text-start rounded-2xl border-2 p-3 transition-colors
                ${active ? 'border-brand-500 bg-brand-50/60' : 'border-ink-200 hover:border-brand-300 bg-white'}`}
              aria-pressed={active}
            >
              <div className={`text-sm font-bold ${active ? 'text-brand-800' : 'text-ink-900'}`}>{m.label}</div>
              <div className="text-xs text-ink-500 mt-0.5">{m.hint}</div>
            </button>
          );
        })}
      </div>

      {needsDate && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className="label mb-0" htmlFor="lb-since">החל מתאריך</label>
          <input
            id="lb-since"
            type="date"
            className="input w-auto"
            value={sinceValue}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => onChange({ metric: 'EARNED_SINCE', since: e.target.value ? new Date(`${e.target.value}T00:00:00`).toISOString() : null })}
          />
          {!sinceValue && <span className="text-xs text-clay-800">בחרו תאריך כדי להציג את הדירוג</span>}
        </div>
      )}

      {saveError && <div className="field-error mt-2">{saveError}</div>}
      {!dirty && (
        <p className="text-xs text-ink-500 mt-3">
          ההגדרה שמורה לתוכנית — כל מנהל רואה את אותו מדד, מכל מחשב. שינוי כאן מציג תצוגה מקדימה; לחצו "שמור" כדי לקבוע.
        </p>
      )}
    </div>
  );
}

/* ───── Podium ───────────────────────────────────────────────── */

// Visual sizing per podium step — gold is tallest in the centre, silver on
// the right (which is the "first" visual slot in RTL), bronze on the left.
const STEP = {
  1: { h: 'h-44', tile: 'bg-gold-gradient', trophy: 'text-gold-500', tag: 'badge bg-gold-500 text-white' },
  2: { h: 'h-36', tile: 'bg-gradient-to-br from-ink-300 to-ink-500', trophy: 'text-ink-400', tag: 'badge bg-ink-400 text-white' },
  3: { h: 'h-28', tile: 'bg-gradient-to-br from-clay-400 to-clay-700', trophy: 'text-clay-600', tag: 'badge bg-clay-600 text-white' },
};

function Podium({ top3, currency }) {
  // Display order: 2 1 3 (visually left to right). In RTL the visual order
  // is reversed automatically by flex-direction, so we render the array
  // and let the browser flip it for us.
  const positions = [top3[1], top3[0], top3[2]].filter(Boolean);
  return (
    <div className="card overflow-hidden">
      <div className="px-5 pt-5 pb-2">
        <p className="eyebrow">פודיום</p>
        <h2 className="h-section mt-1">שלושת המובילים</h2>
      </div>
      <div className="px-5 pb-5 pt-2 grid grid-cols-3 gap-3 items-end">
        {positions.map((s) => (
          <PodiumStep key={s.id} s={s} currency={currency} />
        ))}
      </div>
    </div>
  );
}

function PodiumStep({ s, currency }) {
  const step = STEP[s.rank] || STEP[3];
  return (
    <div className="flex flex-col items-center text-center animate-pop-in">
      <span className={step.tag}>#{s.rank}</span>
      <div className="my-2 w-14 h-14 rounded-full bg-white border-2 border-white shadow-pop grid place-items-center">
        <TrophyIcon className={`w-7 h-7 ${step.trophy}`} />
      </div>
      <div className="text-sm font-bold text-ink-900 truncate w-full px-1">
        {s.firstName} {s.lastName}
      </div>
      {s.className && (
        <div className="text-[11px] text-ink-500 mb-1">{s.className}</div>
      )}
      <div className={`w-full rounded-t-2xl shadow-card ${step.h} ${step.tile} mt-1 relative overflow-hidden`}>
        <div className="absolute inset-0 grid place-items-center text-white">
          <span className="text-lg font-extrabold num drop-shadow">{formatMoney(s.score, currency)}</span>
        </div>
      </div>
    </div>
  );
}

/* ───── Rank row ─────────────────────────────────────────────── */

function RankRow({ s, leaderScore, currency, metric }) {
  const pct = Math.max(0, Math.min(100, Math.round(((s.score || 0) / leaderScore) * 100)));
  return (
    <li className="px-4 py-3 hover:bg-ink-50/60 transition-colors">
      <div className="flex items-center gap-3">
        <span className="num text-sm font-bold text-ink-500 w-10 text-center">#{s.rank}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink-900 truncate">
              {s.firstName} {s.lastName}
            </span>
            {s.className && <span className="badge-slate">{s.className}</span>}
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-ink-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-gradient transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="num text-end shrink-0">
          <div className="font-bold text-ink-900">{formatMoney(s.score, currency)}</div>
          <div className="text-[11px] text-ink-500">
            {/* Secondary line: the two raw tallies; when ranking by something
                other than balance, show the current balance too so the row
                still answers "how many points do they have right now". */}
            {metric !== 'BALANCE' && <span className="text-ink-700">יתרה {formatMoney(s.balance, currency)}</span>}
            {metric !== 'BALANCE' && (s.credit > 0 || s.debit > 0) && <span aria-hidden> · </span>}
            {s.credit > 0 && <span className="text-mint-700">+{formatMoney(s.credit, currency)}</span>}
            {s.credit > 0 && s.debit > 0 && <span aria-hidden> · </span>}
            {s.debit > 0 && <span className="text-sky-700">−{formatMoney(s.debit, currency)}</span>}
          </div>
        </div>
      </div>
    </li>
  );
}
